import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  li_fat_id?: string;
}

interface ConversionEventData {
  event_type: string;
  page_path?: string;
  metadata?: Record<string, unknown>;
}

// Session ID storage key (sessionStorage, not cookies)
const SESSION_ID_KEY = "sf_conversion_session";
const UTM_PARAMS_KEY = "sf_utm_params";

/**
 * Generate a fingerprint-based session ID (no cookies needed)
 * Uses browser characteristics that are consistent within a session
 */
async function generateSessionId(): Promise<string> {
  const fingerprint = [
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.maxTouchPoints || 0,
    // Add timestamp bucket (changes every 30 minutes for session grouping)
    Math.floor(Date.now() / (30 * 60 * 1000)),
  ].join("|");

  // Hash the fingerprint
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("").slice(0, 32);
}

/**
 * Get or create session ID
 */
async function getSessionId(): Promise<string> {
  // Try to get from sessionStorage first (persists within tab session)
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  
  if (!sessionId) {
    sessionId = await generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  
  return sessionId;
}

/**
 * Extract UTM params from URL
 */
function extractUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);
  
  const utmParams: UTMParams = {};
  
  if (params.get("utm_source")) utmParams.utm_source = params.get("utm_source")!;
  if (params.get("utm_medium")) utmParams.utm_medium = params.get("utm_medium")!;
  if (params.get("utm_campaign")) utmParams.utm_campaign = params.get("utm_campaign")!;
  if (params.get("utm_content")) utmParams.utm_content = params.get("utm_content")!;
  if (params.get("utm_term")) utmParams.utm_term = params.get("utm_term")!;
  if (params.get("gclid")) utmParams.gclid = params.get("gclid")!;
  if (params.get("fbclid")) utmParams.fbclid = params.get("fbclid")!;
  if (params.get("li_fat_id")) utmParams.li_fat_id = params.get("li_fat_id")!;
  
  return utmParams;
}

/**
 * Store UTM params in sessionStorage (captures on first page load)
 */
function captureUTMParams(): UTMParams {
  // Check if we already captured UTM params this session
  const stored = sessionStorage.getItem(UTM_PARAMS_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  
  // Extract from URL
  const params = extractUTMParams();
  
  // Only store if we have at least one param
  if (Object.keys(params).length > 0) {
    sessionStorage.setItem(UTM_PARAMS_KEY, JSON.stringify(params));
  }
  
  return params;
}

/**
 * Get stored UTM params
 */
function getStoredUTMParams(): UTMParams {
  const stored = sessionStorage.getItem(UTM_PARAMS_KEY);
  return stored ? JSON.parse(stored) : {};
}

/**
 * Send conversion event to server
 */
async function sendConversionEvent(
  sessionId: string,
  event: ConversionEventData,
  utmParams: UTMParams
): Promise<boolean> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    const response = await fetch(`${supabaseUrl}/functions/v1/track-conversion`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_id: sessionId,
        event_type: event.event_type,
        page_path: event.page_path || window.location.pathname,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        metadata: event.metadata || {},
        ...utmParams,
      }),
    });

    if (!response.ok) {
      console.warn("[Conversion] Failed to track event:", response.status);
      return false;
    }

    return true;
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.warn("[Conversion] Error tracking event:", error);
    return false;
  }
}

/**
 * Hook for cookieless conversion tracking
 * 
 * Usage:
 * ```tsx
 * const { trackEvent, trackPageView, trackCTAClick } = useServerConversion();
 * 
 * // Track a page view (automatic on mount if autoTrackPageView is true)
 * trackPageView();
 * 
 * // Track a CTA click
 * <button onClick={() => trackCTAClick("hero_cta", "Start Free Trial")}>
 *   Start Free Trial
 * </button>
 * ```
 */
export function useServerConversion(options: { autoTrackPageView?: boolean } = {}) {
  const { autoTrackPageView = false } = options;
  const location = useLocation();
  const hasTrackedPageView = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const utmParamsRef = useRef<UTMParams>({});

  // Initialize session ID and capture UTM params on mount
  useEffect(() => {
    const init = async () => {
      sessionIdRef.current = await getSessionId();
      utmParamsRef.current = captureUTMParams();
    };
    init();
  }, []);

  // Auto-track page view on location change
  useEffect(() => {
    if (autoTrackPageView && !hasTrackedPageView.current && sessionIdRef.current) {
      hasTrackedPageView.current = true;
      sendConversionEvent(
        sessionIdRef.current,
        { event_type: "page_view", page_path: location.pathname },
        utmParamsRef.current
      );
    }
  }, [location.pathname, autoTrackPageView]);

  /**
   * Track a generic conversion event
   */
  const trackEvent = useCallback(async (
    eventType: string,
    metadata?: Record<string, unknown>
  ): Promise<boolean> => {
    const sessionId = sessionIdRef.current || await getSessionId();
    const utmParams = utmParamsRef.current || getStoredUTMParams();
    
    return sendConversionEvent(sessionId, {
      event_type: eventType,
      page_path: window.location.pathname,
      metadata,
    }, utmParams);
  }, []);

  /**
   * Track a page view
   */
  const trackPageView = useCallback(async (customPath?: string): Promise<boolean> => {
    const sessionId = sessionIdRef.current || await getSessionId();
    const utmParams = utmParamsRef.current || getStoredUTMParams();
    
    return sendConversionEvent(sessionId, {
      event_type: "page_view",
      page_path: customPath || window.location.pathname,
    }, utmParams);
  }, []);

  /**
   * Track a CTA click
   */
  const trackCTAClick = useCallback(async (
    buttonLocation: string,
    buttonText?: string
  ): Promise<boolean> => {
    const sessionId = sessionIdRef.current || await getSessionId();
    const utmParams = utmParamsRef.current || getStoredUTMParams();
    
    return sendConversionEvent(sessionId, {
      event_type: "cta_click",
      metadata: {
        button_location: buttonLocation,
        button_text: buttonText,
      },
    }, utmParams);
  }, []);

  /**
   * Track signup page view
   */
  const trackSignupPageView = useCallback(async (): Promise<boolean> => {
    const sessionId = sessionIdRef.current || await getSessionId();
    const utmParams = utmParamsRef.current || getStoredUTMParams();
    
    return sendConversionEvent(sessionId, {
      event_type: "signup_page_view",
      page_path: "/auth",
    }, utmParams);
  }, []);

  /**
   * Track signup started (form submitted)
   */
  const trackSignupStarted = useCallback(async (
    emailDomain?: string
  ): Promise<boolean> => {
    const sessionId = sessionIdRef.current || await getSessionId();
    const utmParams = utmParamsRef.current || getStoredUTMParams();
    
    return sendConversionEvent(sessionId, {
      event_type: "signup_started",
      metadata: {
        email_domain: emailDomain, // Anonymized - just the domain
      },
    }, utmParams);
  }, []);

  /**
   * Track signup completed
   */
  const trackSignupCompleted = useCallback(async (
    userId?: string
  ): Promise<boolean> => {
    const sessionId = sessionIdRef.current || await getSessionId();
    const utmParams = utmParamsRef.current || getStoredUTMParams();
    
    return sendConversionEvent(sessionId, {
      event_type: "signup_completed",
      metadata: {
        user_id: userId,
      },
    }, utmParams);
  }, []);

  /**
   * Get current session ID (for linking after signup)
   */
  const getConversionSessionId = useCallback(async (): Promise<string> => {
    return sessionIdRef.current || await getSessionId();
  }, []);

  return {
    trackEvent,
    trackPageView,
    trackCTAClick,
    trackSignupPageView,
    trackSignupStarted,
    trackSignupCompleted,
    getConversionSessionId,
  };
}
